<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <img
        :src="`${staticUrl}t_img/ohms-law-mage.svg`"
        alt="Ohms Law Mage Image"
        width="150"
        height="150"
        class="w-full !max-w-96 h-auto block mb-2.5 mx-auto"
        loading="lazy" />
      <div class="font-bold text-center">Fill in two values to determine the other two</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Amps (I)</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.amps}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.amps}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.amps}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.amps) }"
            v-model="state.amps"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.amps, state.amps)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Volts (E)</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.volts}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.volts}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.volts}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.volts) }"
            v-model="state.volts"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.volts, state.volts)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Watts (W)</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.watts}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.watts) }"
            v-model="state.watts"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.watts, state.watts)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Ohms (R)</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.ohms}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.ohms}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.ohms}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.ohms) }"
            v-model="state.ohms"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.ohms, state.ohms)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

const staticUrl = STATIC_URL || '/static/';

enum CalculatorFieldsTypes {
  amps = 'amps',
  volts = 'volts',
  watts = 'watts',
  ohms = 'ohms',
}

const state = reactive({
  amps: '' as string | number,
  volts: '' as string | number,
  watts: '' as string | number,
  ohms: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [
  CalculatorFieldsTypes.amps,
  CalculatorFieldsTypes.volts,
  CalculatorFieldsTypes.watts,
  CalculatorFieldsTypes.ohms,
];
const calcType: string = 'ohms-law-calculator-calculator';
const minInputFieldsQty: number = 2;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.amps = '';
  state.volts = '';
  state.watts = '';
  state.ohms = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.outputFields.length === minInputFieldsQty) {
    const ampsValue = Number(state.amps);
    const voltsValue = Number(state.volts);
    const wattsValue = Number(state.watts);
    const ohmsValue = Number(state.ohms);

    if (state.outputFields.includes(CalculatorFieldsTypes.watts) && state.outputFields.includes(CalculatorFieldsTypes.ohms)) {
      state.watts = GlobalHelper.roundToDecimals(ampsValue * voltsValue, 4);
      state.ohms = GlobalHelper.roundToDecimals(voltsValue / ampsValue, 4);
    }

    if (state.outputFields.includes(CalculatorFieldsTypes.volts) && state.outputFields.includes(CalculatorFieldsTypes.ohms)) {
      state.volts = GlobalHelper.roundToDecimals(wattsValue / ampsValue, 4);
      state.ohms = GlobalHelper.roundToDecimals(wattsValue / (ampsValue * ampsValue), 4);
    }

    if (state.outputFields.includes(CalculatorFieldsTypes.watts) && state.outputFields.includes(CalculatorFieldsTypes.volts)) {
      state.watts = GlobalHelper.roundToDecimals(ampsValue * ampsValue * ohmsValue, 4);
      state.volts = GlobalHelper.roundToDecimals(ampsValue * ohmsValue, 4);
    }

    if (state.outputFields.includes(CalculatorFieldsTypes.amps) && state.outputFields.includes(CalculatorFieldsTypes.ohms)) {
      state.amps = GlobalHelper.roundToDecimals(wattsValue / voltsValue, 4);
      state.ohms = GlobalHelper.roundToDecimals((voltsValue * voltsValue) / wattsValue, 4);
    }

    if (state.outputFields.includes(CalculatorFieldsTypes.watts) && state.outputFields.includes(CalculatorFieldsTypes.amps)) {
      state.watts = GlobalHelper.roundToDecimals((voltsValue * voltsValue) / ohmsValue, 4);
      state.amps = GlobalHelper.roundToDecimals(voltsValue / ohmsValue, 4);
    }

    if (state.outputFields.includes(CalculatorFieldsTypes.amps) && state.outputFields.includes(CalculatorFieldsTypes.volts)) {
      state.amps = GlobalHelper.roundToDecimals(Math.sqrt(wattsValue / ohmsValue), 4);
      state.volts = GlobalHelper.roundToDecimals(Math.sqrt(wattsValue * ohmsValue), 4);
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
